import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Home, LogOut, RefreshCw } from 'lucide-react';
import SettingsDropdown from '@/components/SettingsDropdown';
import NotificationDropdown from '@/components/NotificationDropdown';
import AppLogo from '@/components/AppLogo';
import { PastelIcon } from '@/utils/appIcons';
import SyncStatusIndicator from '@/components/SyncStatusIndicator';

/**
 * GlobalHeader - Consistent navigation header for all module pages
 * 
 * Features:
 * - Back button (←): Navigate to previous screen, preserves history
 * - Home button (🏠): Always goes directly to Home Screen
 * - Refresh button (🔄): Refresh current page data
 * - Module icon (supports both Lucide icons and 3D pastel image icons)
 * - User info and logout
 */
const GlobalHeader = ({ 
  icon: Icon, 
  iconSrc, // 3D pastel icon URL
  iconBgColor = '#E0DCD3',
  iconColor = 'text-gray-700',
  title, 
  subtitle,
  onRefresh, // Callback to refresh page data
  children // For additional header actions
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Check if we're on the home page
  const isHomePage = location.pathname === '/';
  
  // Check if there's navigation history to go back to
  const canGoBack = window.history.length > 1 && !isHomePage;

  const handleBack = () => {
    navigate(-1);
  };

  const handleHome = () => {
    navigate('/');
  };

  const handleRefresh = async () => {
    if (onRefresh) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
        <div className="flex justify-between items-center">
          {/* Left side: Back button + Logo + Module info */}
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            {/* Back button - only show on sub-pages */}
            {canGoBack && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 flex-shrink-0"
                data-testid="back-button"
                aria-label="Go back"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            
            {/* App Logo - clickable to go home */}
            <button 
              onClick={handleHome}
              className="flex-shrink-0 hover:opacity-80 transition-opacity"
              aria-label="Go to home"
            >
              <AppLogo size="small" />
            </button>
            
            {/* Module divider and title with icon */}
            {title && (
              <>
                <span className="text-gray-300 hidden sm:inline">|</span>
                <div className="flex items-center gap-2 min-w-0 hidden sm:flex">
                  {/* Module icon - supports both Lucide and 3D pastel icons */}
                  {iconSrc ? (
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: iconBgColor }}
                    >
                      <PastelIcon src={iconSrc} alt={title} size={24} />
                    </div>
                  ) : Icon && (
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: iconBgColor }}
                    >
                      <Icon className={`h-4 w-4 ${iconColor}`} />
                    </div>
                  )}
                  <h1 className="text-sm sm:text-base font-medium text-gray-700 truncate">{title}</h1>
                </div>
              </>
            )}
          </div>
          
          {/* Right side: Additional actions + Refresh + Home + User + Logout */}
          <div className="flex items-center space-x-1 sm:space-x-3 flex-shrink-0">
            {/* Additional header actions passed as children - hidden on small mobile */}
            <div className="hidden sm:flex items-center space-x-2">
              {children}
            </div>
            
            {/* Refresh button - only show if onRefresh callback provided */}
            {onRefresh && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="p-2 sm:px-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                data-testid="refresh-button"
                aria-label="Refresh data"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline ml-2">Refresh</span>
              </Button>
            )}
            
            {/* Home button - always visible except on home page */}
            {!isHomePage && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleHome}
                className="p-2 sm:px-3 text-gray-600 hover:text-gray-900"
                data-testid="home-button"
                aria-label="Go to home"
              >
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">Home</span>
              </Button>
            )}
            
            {/* User info - hidden on mobile */}
            {user && (
              <div className="hidden lg:flex items-center space-x-2">
                {user.picture && (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="h-8 w-8 rounded-full"
                  />
                )}
                <span className="text-sm font-medium text-gray-700 max-w-[120px] truncate">
                  {user.name}
                </span>
              </div>
            )}
            
            {/* Sync Status Indicator with Sync Now button */}
            <div className="flex-shrink-0">
              <SyncStatusIndicator size="sm" showSyncButton={true} />
            </div>
            
            {/* Notifications */}
            <NotificationDropdown />
            
            {/* Settings dropdown */}
            <SettingsDropdown />
            
            {/* Logout button */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="p-2 sm:px-3 text-gray-500 hover:text-gray-700"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline ml-2">Logout</span>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default GlobalHeader;
